/**
 * O que o "modo de inferência" de fato oferece de escolha.
 *
 * ## Por que existe
 *
 * O catálogo do servidor lista cinco modos — `realtime`, `balanced`,
 * `precision`, `quality`, `batch` — e a remedição de 17/08 mostrou que quatro
 * deles devolvem o **mesmo arquivo, byte a byte**: md5 idêntico em 6 réplicas
 * com a ordem rotacionada. Só `realtime` produz áudio distinto. A latência
 * também não separa nada: as cinco medianas cabem em 58 ms, contra uma cauda de
 * ~1.250 ms *dentro* de um único modo.
 *
 * Ou seja: a tela oferecia cinco alavancas para dois comportamentos. É a mesma
 * armadilha que este projeto já pagou com o `speed` do OmniVoice — uma alavanca
 * que mente é pior que uma que não existe. Este módulo reduz os cinco nomes às
 * duas coisas que a pessoa pode de fato escolher, e a bancada desenha só isso.
 *
 * ## Por que a redução é CALCULADA, e não escrita aqui
 *
 * Quem sabe quais modos mudam o áudio é o servidor, que publica `audioDistinto`
 * por modo. Escrever "o distinto é o `realtime`" nesta pasta criaria uma segunda
 * verdade no navegador, que divergiria da do servidor no dia em que a medição
 * fosse refeita — é o mesmo motivo pelo qual `escolha.ts` recusa ter uma lista
 * de vozes de reserva no cliente.
 *
 * Quando o catálogo não sustenta a redução (servidor velho que não publica o
 * campo, nenhum modo distinto, ou mais de um), `reduzirModos` devolve `null` e
 * diz honestamente que não sabe. A bancada então cai na lista completa, que é
 * feia mas verdadeira, em vez de inventar uma dicotomia que a medição não
 * autoriza.
 *
 * ## O que este módulo deliberadamente NÃO faz
 *
 * Não reescreve a escolha gravada. Quem tem `quality` salvo continua mandando
 * `quality` até tocar no seletor: trocar por baixo seria mudar o que a pessoa
 * pediu sem ela saber, e a diferença entre "eu não ofereço mais isso" e "eu
 * decidi outra coisa por você" é justamente a que interessa preservar.
 */

import type { Catalogo } from './escolha';

/** As duas coisas que existem para ouvir. Nomes de família, não de modo. */
export type FamiliaDeAudio = 'padrao' | 'alternativo';

export interface SeletorDeAudio {
  /**
   * O que gravar quando a pessoa escolhe "Padrão".
   *
   * `null` significa "não mande `mode` nenhum e deixe o servidor decidir" — é o
   * caso comum, e o certo: quem escolhe o áudio de sempre está dizendo que não
   * quer opinar, então uma troca futura de `OMNIVOICE_MODE` deve alcançá-lo.
   */
  padrao: string | null;
  /** O único modo que devolve arquivo diferente. */
  alternativo: string;
  /** Os ids que devolvem o MESMO arquivo. Ficam na tela como prova, não como opção. */
  identicos: string[];
  /** Aviso do catálogo sobre o modo distinto, se o servidor publicar algum. */
  aviso?: string;
}

export function reduzirModos(catalogo: Catalogo | null): SeletorDeAudio | null {
  if (catalogo === null) return null;

  const distintos = catalogo.modos.filter((m) => m.audioDistinto === true);
  const identicos = catalogo.modos.filter((m) => m.audioDistinto === false);

  // A redução só é honesta quando o catálogo descreve exatamente dois
  // comportamentos. Um servidor que não publica `audioDistinto` cai aqui — e é
  // o que se quer: sem medição não há como rotular a escolha com verdade.
  if (distintos.length !== 1 || identicos.length === 0) return null;

  const padraoDoServidor = catalogo.modos.find((m) => m.id === catalogo.modoPadrao);

  const seletor: SeletorDeAudio = {
    // Se o padrão do servidor já é do time do "mesmo arquivo", "Padrão" quer
    // dizer "o que o servidor decidir" e nada é enviado. Se o padrão do servidor
    // passar a ser o modo distinto, "Padrão" precisa virar explícito — senão o
    // rótulo passaria a mentir sozinho, sem ninguém ter mudado uma linha aqui.
    padrao:
      padraoDoServidor !== undefined && padraoDoServidor.audioDistinto === false
        ? null
        : identicos[0].id,
    alternativo: distintos[0].id,
    identicos: identicos.map((m) => m.id),
  };
  if (distintos[0].aviso !== undefined) seletor.aviso = distintos[0].aviso;
  return seletor;
}

/**
 * Em que família cai o que está gravado — inclusive quando nada está.
 *
 * O servidor valida `mode` contra uma lista fechada e ignora em silêncio o que
 * não reconhece, caindo no próprio padrão. Um id gravado que sumiu do catálogo,
 * portanto, soa exatamente igual a `null`, e é assim que ele aparece aqui: a
 * tela mostra o que a pessoa vai OUVIR, não o que ela digitou um dia.
 */
export function familiaDe(modo: string | null, catalogo: Catalogo): FamiliaDeAudio {
  const conhecido = modo !== null && catalogo.modos.some((m) => m.id === modo);
  const efetivo = conhecido ? modo : catalogo.modoPadrao;
  const entrada = catalogo.modos.find((m) => m.id === efetivo);
  return entrada !== undefined && entrada.audioDistinto === true ? 'alternativo' : 'padrao';
}

/**
 * O que precisa ser CONTADO a quem já tinha escolhido.
 *
 * Uma escolha antiga não pode ficar num estado inválido nem ser trocada em
 * silêncio. Como ela continua valendo e continua soando igual, o que resta é
 * dizer isso em voz alta e deixar a normalização a um toque de distância.
 */
export type Pendencia =
  | { tipo: 'nenhuma' }
  /** Um dos quatro nomes que saíram da lista. Continua valendo e soa igual ao padrão. */
  | { tipo: 'aposentado'; modo: string }
  /** Nem no catálogo está: o servidor descarta e fala com o padrão dele. */
  | { tipo: 'fora-do-catalogo'; modo: string };

export function pendenciaDa(
  modo: string | null,
  catalogo: Catalogo,
  seletor: SeletorDeAudio,
): Pendencia {
  if (modo === null) return { tipo: 'nenhuma' };
  if (modo === seletor.padrao || modo === seletor.alternativo) return { tipo: 'nenhuma' };
  if (!catalogo.modos.some((m) => m.id === modo)) return { tipo: 'fora-do-catalogo', modo };
  return { tipo: 'aposentado', modo };
}
